import { Link } from 'react-router-dom';

interface ButtonProps {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  href?: string;
  to?: string;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  className?: string;
  icon?: string;
}

export default function Button({
  children,
  variant = 'primary',
  size = 'md',
  href,
  to,
  onClick,
  type = 'button',
  disabled = false,
  className = '',
  icon,
}: ButtonProps) {
  const baseStyles = 'inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed';
  
  const variantStyles = {
    primary: 'bg-olive-600 hover:bg-olive-700 text-white',
    secondary: 'bg-sand-200 hover:bg-sand-300 text-earth-800',
    outline: 'border-2 border-olive-600 text-olive-700 hover:bg-olive-50',
    ghost: 'text-earth-700 hover:bg-sand-100',
  };

  const sizeStyles = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg',
  };

  const combinedStyles = `${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${className}`;

  const content = (
    <>
      {children}
      {icon && <i className={`${icon}`}></i>}
    </>
  );

  if (href) {
    // Links internos (âncoras) não devem abrir em nova aba
    const isInternalAnchor = href.startsWith('#');
    return (
      <a 
        href={href} 
        className={combinedStyles} 
        target={isInternalAnchor ? undefined : "_blank"} 
        rel={isInternalAnchor ? undefined : "noopener noreferrer"}
      >
        {content}
      </a>
    );
  }

  if (to) {
    return (
      <Link to={to} className={combinedStyles}>
        {content}
      </Link>
    );
  }

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={combinedStyles}
    >
      {content}
    </button>
  );
}
